/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { html, View } from 'rune-ts';
import { Modal } from '@services/MarppleShop/renderApp/components/atoms/Modal/Modal.ts';
import { font } from '@services/MarppleShop/shared/typography';
import klass from './CreatorShopHomeBottomSheetView.module.scss';
import { classes } from '@services/MarppleShop/shared/util';
import { IconBasicCloseMIcon } from '@services/MarppleShop/shared/components/atoms/Icon';
import { ASSET_URLS } from '@services/MarppleShop/shared/constant/asset_urls.ts';
import { convertNewLinesToBr } from '@services/MarppleShop/shared/util/convertNewLinesToBr.ts';
import { getSnsIcon } from '@services/MarppleShop/shared/util/snsIcon.ts';
import { EBestCreatorRankingType } from '@services/MarppleShop/shared/enum/EBestCreatorRankingType.ts';
import { differenceInMinutes, getMonth, getYear, parseISO } from 'date-fns';
import { MembershipUtilS } from '@services/MarppleShop/features/Membership/outbound/share.ts';
import { makeQueryString } from '../../../../../../shared/utils/makeString/makeQueryString.ts';
import differenceInCalendarWeeks from 'date-fns/differenceInCalendarWeeks/index.js';
import startOfMonth from 'date-fns/startOfMonth/index.js';
import { makeEmptyScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { dataStr } from '@services/MarppleShop/shared/util/dataStr.ts';

const stripProtocolAndWww = (url: string) => url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '');

type TCreatorShopHomeBottomSheetViewProps = {
  creator: {
    name: string;
    description: string;
    sns_url: string;
  };
  best_store_ranking:
    | {
        current_rank: number;
        previous_rank: number | null;
        date: string;
        type: EBestCreatorRankingType;
      }
    | undefined;
  membership:
    | {
        ciety: {
          subdomain: string;
        } | null;
      }
    | undefined;
};

export class CreatorShopHomeBottomSheetView extends View<TCreatorShopHomeBottomSheetViewProps> {
  private modal_view;

  constructor(props: TCreatorShopHomeBottomSheetViewProps) {
    super(props);

    const { best_store_ranking, creator, membership } = props;

    const sns_icon = getSnsIcon(creator.sns_url);

    const ciety_icon = membership?.ciety
      ? getSnsIcon(MembershipUtilS.getCietyUrl(`/@${membership.ciety.subdomain}`))
      : null;

    const ranking = best_store_ranking?.current_rank;

    const ranking_texts_by_type: Record<EBestCreatorRankingType, string> = {
      [EBestCreatorRankingType.Monthly]: ET('creator_shop::home_modal::monthly_ranking', { ranking }),
      [EBestCreatorRankingType.Weekly]: ET('creator_shop::home_modal::weekly_ranking', { ranking }),
      [EBestCreatorRankingType.Realtime]: ET('creator_shop::home_modal::realtime_ranking', { ranking }),
    };

    const parsed_best_store_ranking_date = best_store_ranking ? parseISO(best_store_ranking.date) : null;

    this.modal_view = new Modal({
      is_bottom_sheet: true,
      isOpen: false,
      size: 'medium',
      body: html`<div class="${klass.modal_small_d}">
        <header class="${klass.modal_headline}">
          <h1 class="${classes(klass.title, font('16_bold'))}">${html.preventEscape(creator.name)}</h1>

          <button class="${klass.close_button}">${IconBasicCloseMIcon}</button>
        </header>

        <div class="${klass.modal_container}">
          <div class="${klass.creator_shop_information_wrapper}">
            ${best_store_ranking && parsed_best_store_ranking_date
              ? html`<a
                  class="${klass.ranking}"
                  href="${`/${ET.lang}/@/creator/ranking/${
                    best_store_ranking.type +
                    (best_store_ranking.type === EBestCreatorRankingType.Weekly ||
                    best_store_ranking.type === EBestCreatorRankingType.Monthly
                      ? `?${makeQueryString({ date: best_store_ranking.date })}`
                      : '')
                  }`}"
                  data-post-message="${dataStr(
                    makeEmptyScreenNavigate(
                      `/${ET.lang}/@/creator/ranking/${
                        best_store_ranking.type +
                        (best_store_ranking.type === EBestCreatorRankingType.Weekly ||
                        best_store_ranking.type === EBestCreatorRankingType.Monthly
                          ? `?${makeQueryString({ date: best_store_ranking.date })}`
                          : '')
                      }`,
                    ),
                  )}"
                >
                  <div class="${klass.trophy_wrapper}">
                    <img
                      alt=""
                      src="${ASSET_URLS.CREATOR_SHOP_HOME.CREATOR_SHOP_HOME_RANKING_TROPHY}"
                      class="${klass.trophy_image}"
                    />
                  </div>

                  <div class="${klass.creator_info_rank}">
                    <span class="${classes(font('16_bold'), klass.weekly_ranking_text)}">
                      ${ranking_texts_by_type[best_store_ranking.type]}
                    </span>
                    <span class="${classes(font('12_medium'), klass.raking_period_text)}">
                      ${{
                        [EBestCreatorRankingType.Monthly]: ET(
                          'creator_shop::home_modal::monthly_recorded_at',
                          {
                            year: getYear(parsed_best_store_ranking_date) % 100,
                            month: getMonth(parsed_best_store_ranking_date) + 1,
                          },
                        ),
                        [EBestCreatorRankingType.Weekly]: ET('creator_shop::home_modal::weekly_recorded_at', {
                          year: getYear(parsed_best_store_ranking_date) % 100,
                          month: getMonth(parsed_best_store_ranking_date) + 1,
                          week:
                            differenceInCalendarWeeks(
                              parsed_best_store_ranking_date,
                              startOfMonth(parsed_best_store_ranking_date),
                              { weekStartsOn: 0 },
                            ) + 1,
                        }),
                        [EBestCreatorRankingType.Realtime]: ET(
                          'creator_shop::home_modal::realtime_recorded_at',
                          {
                            minute: differenceInMinutes(new Date(), parsed_best_store_ranking_date),
                          },
                        ),
                      }[best_store_ranking.type]}
                    </span>
                  </div>
                </a>`
              : null}
            ${creator.description
              ? html`<p class="${klass.creator_shop_information} ${font('14_medium')}">
                  ${html.preventEscape(convertNewLinesToBr(creator.description))}
                </p>`
              : null}
          </div>

          <div class="${klass.sns_list_wrapper}">
            ${creator.sns_url && !creator.sns_url.includes('ciety')
              ? html`<div class="${klass.sns_item}">
                  <div class="${klass.button_sns}">${sns_icon.tmpl}</div>

                  <div class="${klass.sns_description}">
                    <span class="${classes(klass.sns_title, font('14_medium'))}"
                      >${html.preventEscape(creator.name)} ${sns_icon.display_name}</span
                    >

                    <a
                      href="${sns_icon.link}"
                      target="_blank"
                      class="${classes(klass.sns_href, font('12_medium'), 'external-url')}"
                      >${stripProtocolAndWww(creator.sns_url)}</a
                    >
                  </div>
                </div>`
              : null}
            ${membership?.ciety
              ? html`<div class="${klass.sns_item}">
                  <div class="${klass.button_sns}">${ciety_icon!.tmpl}</div>

                  <div class="${klass.sns_description}">
                    <span class="${classes(klass.sns_title, font('14_medium'))}"
                      >${html.preventEscape(creator.name)} ${ciety_icon!.display_name}</span
                    >

                    <a
                      href="${ciety_icon!.link}"
                      target="_blank"
                      class="${classes(klass.sns_href, font('12_medium'), 'external-url')}"
                      >${stripProtocolAndWww(
                        MembershipUtilS.getCietyUrl(`/@${membership.ciety.subdomain}`),
                      )}</a
                    >
                  </div>
                </div>`
              : null}
          </div>
        </div>
      </div> `,
    });
  }

  protected override onRender() {
    this.delegate('click', `.${klass.close_button}`, () => {
      this.modal_view.close();
    });
  }

  open() {
    this.modal_view.open();
  }

  protected override template() {
    return html`<div>${this.modal_view}</div>`;
  }
}
